import { getApiPrefix } from '../utils/apiHelper';
import { useState, useEffect, useCallback } from "react";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { SearchableSelect } from "../components/ui/searchable-select";
import { Checkbox } from "../components/ui/checkbox";
import { Plus, Trash2, Building2, Loader2, Pencil, ArrowRightLeft } from "lucide-react";
import { usePermissions } from "../hooks/usePermissions";
import DataTable from "../components/ui/data-table";
import { PageLoader } from "../components/ui/page-loader";
import { handleApiError, showSuccess } from "../utils/errorHandler";
import { useTranslation } from "../contexts/I18nContext";

export default function CostCentersPage() {
  const { t } = useTranslation();
  const { canEdit } = usePermissions();
  const canEditCDC = canEdit("cdc");
  
  const [centers, setCenters] = useState([]);
  const [conti, setConti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ codice: "", nome: "", descrizione: "", conto_id: "", da_ripartire: false });
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [res, contiRes] = await Promise.all([
        api.get(`${getApiPrefix()}/cost-centers`),
        api.get(`${getApiPrefix()}/accounts`).catch(() => ({ data: [] }))
      ]);
      setCenters(res.data);
      setConti(contiRes.data || []);
    } catch (err) {
      handleApiError(err, t("errors.loadCostCenters"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmit = async () => {
    if (!form.codice || !form.nome) {
      handleApiError({ message: t("errors.requiredCodeName") });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        codice: form.codice,
        nome: form.nome,
        descrizione: form.descrizione || null,
        conto_id: form.conto_id || null,
        da_ripartire: form.da_ripartire || false
      };
      
      if (editingId) {
        await api.put(`${getApiPrefix()}/cost-centers/${editingId}`, payload);
        showSuccess(t("success.costCenterUpdated"));
      } else {
        await api.post(`${getApiPrefix()}/cost-centers`, payload);
        showSuccess(t("success.costCenterCreated"));
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ codice: "", nome: "", descrizione: "", conto_id: "", da_ripartire: false });
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorSaveCostCenter"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("confirm.deleteCostCenter"))) return;
    try {
      await api.delete(`${getApiPrefix()}/cost-centers/${id}`);
      showSuccess(t("success.costCenterDeleted"));
      fetchData();
    } catch (err) {
      handleApiError(err, t("errors.errorDeleteCostCenter"));
    }
  };

  const handleBulkDelete = async (ids) => {
    let deleted = 0;
    for (const id of ids) {
      try {
        await api.delete(`${getApiPrefix()}/cost-centers/${id}`);
        deleted++;
      } catch (err) { }
    }
    showSuccess(`${deleted} centri di costo eliminati`);
    fetchData();
  };

  const openEdit = (center) => {
    setEditingId(center.id);
    setForm({ 
      codice: center.codice, 
      nome: center.nome, 
      descrizione: center.descrizione || "",
      conto_id: center.conto_id || "",
      da_ripartire: center.da_ripartire || false
    });
    setDialogOpen(true);
  };

  const columns = [
    { key: 'codice', label: t('common.code'), width: '100px' },
    { key: 'nome', label: t('common.name') },
    { 
      key: 'conto_nome', 
      label: 'Conto di Costo',
      render: (_, row) => row.conto_codice ? `[${row.conto_codice}] ${row.conto_nome || ''}` : '-'
    },
    { 
      key: 'da_ripartire', 
      label: 'Da Ripartire',
      width: '100px',
      render: (_, row) => row.da_ripartire ? 
        <span className="inline-flex items-center gap-1 text-orange-600"><ArrowRightLeft className="h-3 w-3" /> Sì</span> : 
        <span className="text-muted-foreground">No</span>
    },
    { key: 'descrizione', label: t('common.description') },
    { 
      key: 'actions', 
      label: t('common.actions'), 
      align: 'right',
      width: '100px',
      sortable: false, 
      filterable: false,
      render: (_, row) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => openEdit(row)} title={t("common.edit")}>
            <Pencil className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => handleDelete(row.id)} title={t("common.delete")}>
            <Trash2 className="h-4 w-4 text-red-500" />
          </Button>
        </div>
      )
    }
  ];

  if (loading) {
    return <PageLoader variant="section" text="Caricamento centri di costo..." />;
  }

  return (
    <div className="space-y-6" data-testid="cost-centers-page">
      <div className="flex items-center gap-3">
        <Building2 className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t("costCenters.title")}</h1>
          <p className="text-muted-foreground text-sm">Gestione centri di costo aziendali</p>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={centers}
        onDelete={canEditCDC ? handleBulkDelete : undefined}
        idField="id"
        emptyMessage="Nessun centro di costo trovato"
        toolbar={
          canEditCDC && (
            <Button onClick={() => { setEditingId(null); setForm({ codice: "", nome: "", descrizione: "", conto_id: "", da_ripartire: false }); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" /> Nuovo Centro di Costo
            </Button>
          )
        }
      />

      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) { setEditingId(null); setForm({ codice: "", nome: "", descrizione: "", conto_id: "", da_ripartire: false }); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingId ? t("costCenters.editCostCenter") : t("costCenters.newCostCenter")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>{t('common.code')} *</Label>
                <Input value={form.codice} onChange={(e) => setForm({ ...form, codice: e.target.value })} placeholder="Es: CC001" />
              </div>
              <div>
                <Label>{t('common.name')} *</Label>
                <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome del centro" />
              </div>
            </div>
            <div>
              <Label>Conto di Costo (opzionale)</Label>
              <SearchableSelect
                options={[
                  { value: "__none__", label: "Nessuno" },
                  ...conti.map(c => ({ value: c.id, label: `${c.codice ? `[${c.codice}] ` : ""}${c.nome}` }))
                ]}
                value={form.conto_id}
                onValueChange={(v) => setForm({ ...form, conto_id: v })}
                placeholder="Seleziona un conto..."
                searchPlaceholder={t("common.searchPlaceholder")}
                testId="select-conto"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox 
                id="da_ripartire" 
                checked={form.da_ripartire} 
                onCheckedChange={(checked) => setForm({ ...form, da_ripartire: checked })}
              />
              <Label htmlFor="da_ripartire" className="cursor-pointer">
                Da ripartire su altri centri di costo
              </Label>
            </div>
            <p className="text-xs text-muted-foreground">
              Se attivato, i costi allocati a questo centro potranno essere ripartiti percentualmente su altri centri di costo
            </p>
            <div>
              <Label>{t("common.description")}</Label>
              <Textarea value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder={t("common.optional")} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSubmit} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingId ? t("common.save") : t("common.create")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
